import React, { useRef, useState } from "react";
import { useUpdateEffect } from "react-use";

import { Box, HStack, Slider, VStack } from "@metrea-ui/v2";

import NumericInputField from "src/components/UI/Input/NumericInputField";
import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { applyFormatToNumber } from "src/utils/formatUtils";
import { mColor } from "src/utils/themeUtils";

import styles from "../Preference/ChooseOneValue.module.css";

const VerticalSliderInput = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const minValue = props.range === "normalized" ? 0 : props.extra.max_budget_decrease * -1;
	const maxValue = props.range === "normalized" ? 20 : props.extra.max_budget_increase * 1;
	const midValue = (maxValue - minValue) / 2;
	const decimalPoint = props.range !== "normalized" ? 0 : 1;
	const currencyUnit = props.range !== "normalized" ? (props.extra?.currencyUnit ?? "") : "";
	const currencySymbol = props.range !== "normalized" ? (props.extra?.currencySymbol ?? "") : "";

	const sliderCursor = props.mode !== "edit" ? "not-allowed" : "pointer";

	const itemValueToSliderValue = (n) => n * midValue + midValue;
	const sliderValueToItemValue = (n) => Number(((n - midValue) / midValue).toFixed(decimalPoint));

	const [items, setItems] = useState(props.items);
	const [values, setValues] = useState(
		getValuesByPropertyPath(props.items, "value").map((x) =>
			props.range === "normalized" ? itemValueToSliderValue(x) : x * 1,
		),
	);
	const [inputValueState, setInputValueState] = useState({
		value: getValuesByPropertyPath(props.items, "value").map((x) =>
			props.range === "normalized" ? itemValueToSliderValue(x) : x * 1,
		)[0],
		version: 0,
	});

	useUpdateEffect(() => {
		setItems(props.items);
		const tempArray = getValuesByPropertyPath(props.items, "value").map((x) =>
			props.range === "normalized" ? itemValueToSliderValue(x) : x * 1,
		);

		setValues(tempArray);
		setInputValueState((prev) => ({
			value: tempArray[0],
			version: prev.version + 1,
		}));
	}, [props.items]);

	const valuetext = (value, index) => {
		let strTemp = typeof items[index].type !== "undefined" ? `${items[index].type}: ` : "";
		strTemp += value >= 0 ? "+" : "";
		strTemp += value;

		if (props.range !== "normalized") {
			strTemp = typeof items[index].type !== "undefined" ? `${items[index].type}: ` : "";
			strTemp += applyFormatToNumber(value, decimalPoint, currencySymbol, currencyUnit);
		}

		return strTemp;
	};

	const sliderChangeHandler = (event, newValue) => {
		if (!Array.isArray(newValue) || newValue.length === 1) {
			const nextValue = newValue[0];
			setValues([nextValue]);
			setInputValueState((prev) => ({
				value: nextValue,
				version: prev.version,
			}));
			return;
		}

		const distance = newValue[1] - newValue[0];
		setValues(newValue);
		setInputValueState((prev) => ({
			value: newValue[0],
			version: prev.version,
		}));

		const newItemValue = newValue.map((x) =>
			props.range === "normalized" ? sliderValueToItemValue(x) : x.toString(),
		);

		if (distance === 0) {
			setItems([...items].reverse());
		}

		const nextItems = items.map((x, index) => ({
			...x,
			value: newItemValue[index],
		}));
		setItems(nextItems);
	};

	const sliderDoneHandler = (event, newValue) => {
		if (!Array.isArray(newValue) || newValue.length === 1) {
			const nextItems = items.map((x, index) => (index === 0 ? { ...x, value: newValue[0] } : x));
			setItems(nextItems);
			props.onChange(nextItems);
			return;
		}

		const nextItems = items.map((x, index) => ({
			...x,
			value: newValue[index],
		}));
		setItems(nextItems);
		props.onChange(nextItems);
	};

	const commitValueToItems = (value) => {
		setInputValueState((prev) => ({
			value,
			version: prev.version + 1,
		}));

		const nextItems = items.map((x) => ({
			...x,
			value,
		}));
		setItems(nextItems);
		props.onChange(nextItems);
	};

	const totalRange = maxValue - minValue;
	const zeroPct = ((0 - minValue) / totalRange) * 100;
	const valuePct = ((values[0] - minValue) / totalRange) * 100;
	const topLabel =
		props.range === "normalized"
			? "+1.0"
			: applyFormatToNumber(maxValue, decimalPoint, currencySymbol, currencyUnit);
	const bottomLabel =
		props.range === "normalized"
			? "-1.0"
			: applyFormatToNumber(minValue, decimalPoint, currencySymbol, currencyUnit);

	return (
		<VStack spacing={1} align="stretch" height={props.height || 200} m={props.m || 2}>
			<Box position="relative" height="100%" display="flex" justifyContent="center">
				<Box
					key={`label-1`}
					position="absolute"
					top={`${((maxValue - values[0]) / (maxValue - minValue)) * 100}%`}
					left="10px"
					transform="translateY(-50%)"
					fontSize="10px"
					bg={values[0] > 0 ? mColor.NavyBlue : mColor.StonyBlue}
					color="white"
					px="4px"
					py="2px"
					borderRadius="4px"
					whiteSpace="nowrap"
					pointerEvents="none"
				>
					{valuetext(values[0], 0)}
				</Box>
				<Slider.Root
					orientation="vertical"
					value={values}
					min={minValue}
					max={maxValue}
					step={props.step || 1}
					disabled={props.mode !== "edit"}
					height="100%"
					onValueChange={(e) => sliderChangeHandler(null, e.value)}
					onValueChangeEnd={(e) => sliderDoneHandler(null, e.value)}
					onKeyUp={(e) => {
						if (e.key === "Enter") {
							sliderDoneHandler(null, values);
						}
					}}
				>
					<Slider.Control height="100%">
						<Slider.Track style={{ cursor: sliderCursor }} />
						<Slider.Thumb
							borderColor={values[0] > 0 ? mColor.NavyBlue : mColor.StonyBlue}
							bg={values[0] > 0 ? mColor.NavyBlue : mColor.StonyBlue}
							borderRadius="full"
							style={{ zIndex: 2, cursor: sliderCursor }}
						/>
					</Slider.Control>

					<Box
						position="absolute"
						left="50%"
						transform="translateX(-50%)"
						width="8px"
						borderRadius="3px"
						bg={values[0] > 0 ? mColor.NavyBlue : mColor.StonyBlue}
						bottom={`${Math.min(zeroPct, valuePct)}%`}
						height={`${Math.abs(valuePct - zeroPct)}%`}
						zIndex={1}
					/>
				</Slider.Root>

				<Box
					position="absolute"
					right="10px"
					top="0"
					height="100%"
					display="flex"
					flexDirection="column"
					justifyContent="space-between"
					fontSize="11px"
					color="gray.600"
					textAlign="left"
					pointerEvents="none"
				>
					<Box>{topLabel}</Box>
					<Box>{bottomLabel}</Box>
				</Box>
			</Box>
			<HStack spacing={0} mt="10px" p="4px">
				<label className={styles.baselineValue}>Change: </label>
				<NumericInputField
					key={inputValueState.version}
					id={`${props.extra.comp_type_id}_${props.extra.id}`}
					name="name"
					label=""
					size="2xs"
					margin="dense"
					value={inputValueState.value}
					min={minValue}
					max={maxValue}
					step={props.step || 1}
					showControls={false}
					disabled={props.mode !== "edit"}
					onCommit={commitValueToItems}
					validationMessage={`Value must be between ${minValue} and ${maxValue}.`}
					onError={props.onError}
					inputProps={{
						formatSpec: {
							kind: "currency",
							symbol: props.extra.currencySymbol || "",
							unit: props.extra.currencyUnit || "",
							decimals: 0,
							fixed: true,
						},
					}}
				/>
			</HStack>
		</VStack>
	);
};

export default VerticalSliderInput;
